/* /billing — trial status, bank-transfer subscription, payment history and tax invoices
 * (prototype billing.js pgBilling). Owner only. */
import { useState } from "react";
import { useBilling } from "../../data/hooks";
import type { BillingView, Cycle, Payment } from "../../data/types";
import { useI18n } from "../../i18n";
import { gDate, moneyText } from "../../lib/format";
import { Alert, Bar, Button, Card, Chip, DualDate, EmptyState, Grid, Kv, Loaded, Money, PageHeader } from "../../ui";
import { PricingPlans } from "../public/components/PricingPlans";
import { quote } from "../public/components/pricingData";
import { InvoiceModal, ReceiptModal } from "./InvoiceModal";
import { SubscribeModal } from "./SubscribeModal";

type Open = { kind: "subscribe"; plan?: string } | { kind: "invoice"; p: Payment } | { kind: "receipt"; p: Payment } | null;

function BillingBody({ v }: { v: BillingView }) {
  const { t, L, lang } = useI18n();
  const [open, setOpen] = useState<Open>(null);
  const [cycle, setCycle] = useState<Cycle>(v.subscription.cycle);
  const s = v.subscription;
  const p = v.payments[0];
  const pending = p?.status === "Pending";
  const rejected = p?.status === "Rejected" && s.status !== "active";
  const active = s.status === "active";
  const next = quote(v.planPrices, s.plan, s.cycle, s.seats, s.aiPack);
  const cycleLabel = (c: Cycle) => (c === "y" ? t("Annually") : t("Monthly"));
  const total = s.trialDays;
  const left = v.trialLeft;

  const statusCard = active ? (
    <Card title={t("Current plan")} testId="plan-card">
      <div className="row between">
        <div><div className="kpi-v">{s.plan}</div><div className="sm muted">{s.seats} {L("employees", "موظفًا")} · {cycleLabel(s.cycle)}</div></div>
        <Chip status="Active" />
      </div>
      <div className="divider" style={{ margin: "14px 0" }} />
      <Kv items={[[L("Active until", "سارية حتى"), s.activeUntil ? <DualDate key="d" date={s.activeUntil} /> : "—"], [L("Next payment", "الدفعة القادمة"), <Money key="m" value={next.total} />], [L("Payment method", "طريقة الدفع"), L("Bank transfer", "تحويل بنكي")]]} />
    </Card>
  ) : (
    <Card title={s.status === "expired" ? L("Free trial ended", "انتهت التجربة المجانية") : L("Free trial", "التجربة المجانية")} testId="trial-card">
      <div className="row between">
        <div>
          <div className="kpi-v" data-testid="trial-left">{left}<span className="kpi-u">{L(`days left of ${total}`, `يومًا متبقية من ${total}`)}</span></div>
          <div className="sm muted">{L("Started ", "بدأت ")}{gDate(s.trialStart, lang)} · {L("ends ", "تنتهي ")}{gDate(v.trialEnd, lang)}</div>
        </div>
        {pending ? <Chip status="Pending" /> : s.status === "expired" ? <Chip status="expired">{L("Ended", "انتهت")}</Chip> : <span className="chip gold">{L("Trial", "تجربة")}</span>}
      </div>
      <div className="mt"><Bar value={total - left} max={total} tone={left <= 5 ? "warn" : "gold"} /></div>
      <p className="sm muted" style={{ margin: "12px 0 0" }}>{L("Everything is unlocked during the trial. Your data stays when you subscribe.", "كل المزايا متاحة خلال التجربة، وتبقى بياناتك عند الاشتراك.")}</p>
    </Card>
  );

  return (
    <div data-testid="billing">
      <PageHeader title={L("Billing & subscription", "الفوترة والاشتراك")} sub={L("Pay by bank transfer from your banking app, then upload the receipt here.", "ادفع بتحويل بنكي من تطبيق البنك ثم ارفع الإيصال هنا.")}
        actions={pending ? null : <Button variant="pri" icon="credit" onClick={() => setOpen({ kind: "subscribe" })} data-testid="btn-subscribe">{active ? L("Renew or change plan", "تجديد أو تغيير الباقة") : L("Subscribe now", "اشترك الآن")}</Button>} />

      {pending && <div className="mb"><Alert tone="info" tint testId="billing-pending" title={L("Receipt received · " + p.id, "تم استلام الإيصال · " + p.id)} desc={L("Our finance team is matching your transfer of " + moneyText(p.paid, lang) + " with our bank statement. You'll get an email and a notification once it's verified.", "يطابق فريقنا المالي تحويلك بمبلغ " + moneyText(p.paid, lang) + " مع كشف الحساب، وستصلك رسالة عند التحقق.")} /></div>}
      {rejected && <div className="mb"><Alert tone="bad" testId="billing-rejected" title={L("Receipt rejected · " + (p.reason ?? ""), "تم رفض الإيصال · " + (p.reason ?? ""))} desc={L("Please upload a clear receipt that shows the amount, date and our reference code.", "يرجى رفع إيصال واضح يظهر المبلغ والتاريخ ورمز المرجع.")} action={<Button variant="sm pri" onClick={() => setOpen({ kind: "subscribe" })} data-testid="btn-reupload">{L("Upload again", "إعادة الرفع")}</Button>} /></div>}
      {!active && !pending && left <= 5 && <div className="mb"><Alert tone="warn" title={left > 0 ? L(`Only ${left} days left in your trial`, `بقي ${left} أيام فقط في التجربة`) : L("Your free trial has ended. Subscribe to keep running payroll.", "انتهت التجربة المجانية. اشترك لمواصلة تشغيل الرواتب.")} /></div>}

      <Grid cols="2-1" className="mb">
        {statusCard}
        <Card title={L("How payment works", "كيف يتم الدفع")}>
          <ol className="steps">
            {([[L("Choose plan", "اختر الباقة"), L("Pick a plan, billing cycle and number of employees.", "اختر الباقة والدورة وعدد الموظفين.")], [L("Transfer from your bank app", "حوّل من تطبيق البنك"), L("Use the IBAN shown and put your reference code in the transfer note.", "استخدم الآيبان المعروض وضع رمز المرجع في وصف التحويل.")], [L("Upload the receipt", "ارفع الإيصال"), L("AI reads the amount, date and reference for you.", "يقرأ الذكاء الاصطناعي المبلغ والتاريخ والمرجع.")], [L("We verify and activate", "نتحقق ونفعّل"), L("Usually within one business day. Your tax invoice is issued automatically.", "عادة خلال يوم عمل، وتصدر الفاتورة الضريبية تلقائيًا.")]] as const).map((x, i) => (
              <li key={i} className="step"><span className="sd">{i + 1}</span><span className="st">{x[0]}</span><span className="sm2">{x[1]}</span></li>
            ))}
          </ol>
        </Card>
      </Grid>

      <Card title={L("Payments & invoices", "المدفوعات والفواتير")} flush testId="payments-card">
        {v.payments.length ? (
          <div className="tw">
            <table className="tbl cards" data-testid="payments-table">
              <thead><tr><th>{L("Reference", "المرجع")}</th><th>{L("Plan", "الباقة")}</th><th className="end">{t("Amount")}</th><th className="hide-md">{L("Submitted", "تاريخ الرفع")}</th><th>{t("Status")}</th><th className="end" aria-label={t("Actions")} /></tr></thead>
              <tbody>
                {v.payments.map((x) => (
                  <tr key={x.id} data-testid={"payment-" + x.id}>
                    <td className="k1"><b className="num">{x.id}</b><div className="xs muted num">{x.ref}</div></td>
                    <td className="x" data-l={L("Plan", "الباقة")}>{x.plan} · {x.seats} · {cycleLabel(x.cycle)}</td>
                    <td className="end k3" data-l={t("Amount")}><Money value={x.total} /></td>
                    <td className="hide-md">{gDate(x.date, lang)}</td>
                    <td className="k2"><Chip status={x.status === "Verified" ? "Paid" : x.status} /></td>
                    <td className="end k3" data-l="">
                      {x.status === "Verified"
                        ? <Button variant="sm" icon="receipt" onClick={() => setOpen({ kind: "invoice", p: x })} data-testid={"btn-invoice-" + x.id}>{L("Tax invoice", "الفاتورة الضريبية")}</Button>
                        : <Button variant="ghost sm" icon="eye" onClick={() => setOpen({ kind: "receipt", p: x })} data-testid={"btn-receipt-" + x.id}>{L("Receipt", "الإيصال")}</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title={L("No payments yet", "لا توجد مدفوعات بعد")} desc={L("When you subscribe, your bank transfer receipts and tax invoices appear here.", "عند الاشتراك ستظهر هنا الإيصالات والفواتير الضريبية.")}
            action={!pending && <div className="mt"><Button variant="sm pri" onClick={() => setOpen({ kind: "subscribe" })}>{L("Subscribe now", "اشترك الآن")}</Button></div>} />
        )}
      </Card>

      <div className="mt">
        <Card title={L("Plans", "الباقات")}>
          <PricingPlans prices={v.planPrices} cycle={cycle} onCycle={setCycle} inApp currentPlan={active ? s.plan : null}
            onChoose={(plan) => (pending ? undefined : setOpen({ kind: "subscribe", plan }))} />
          {pending && <p className="xs muted" style={{ margin: "8px 0 0" }}>{L("You can change plan after your pending payment is verified.", "يمكنك تغيير الباقة بعد التحقق من الدفعة المعلقة.")}</p>}
        </Card>
      </div>

      {open?.kind === "subscribe" && <SubscribeModal view={v} initialPlan={open.plan} onClose={() => setOpen(null)} />}
      {open?.kind === "invoice" && <InvoiceModal p={open.p} onClose={() => setOpen(null)} />}
      {open?.kind === "receipt" && <ReceiptModal p={open.p} onClose={() => setOpen(null)} />}
    </div>
  );
}

export default function Billing() {
  const q = useBilling();
  return <Loaded q={q}>{(v) => <BillingBody v={v} />}</Loaded>;
}
